'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { FederatedPhoto } from '@/src/components/FederatedPhoto';
import { ShowMoreContent } from '@/src/components/features/property/ui/ShowMoreContent';
import { getSourceInfoDisplay } from './sourceInfoModule.utils';
import * as styles from './sourceInfoModule.styles';

type Props = {
  listing: PropertyDetailListing;
};

const MLS_LOGO_SIZE = 192;

export const SourceInfoModule = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const display = useMemo(() => getSourceInfoDisplay(listing), [listing]);
  const mlsHeading = display.mlsName || t('sourceMlsHeading');

  if (!display.hasBrokerInfo && !display.hasMlsInfo) {
    return null;
  }

  return (
    <ShowMoreContent
      controlsId="source-info-module-details"
      showMoreLabel={t('showMore')}
      showLessLabel={t('showLess')}
      showMoreAriaLabel={`${t('showMore')}: ${t('sourceInfoTitle')}`}
      showLessAriaLabel={`${t('showLess')}: ${t('sourceInfoTitle')}`}
    >
      <div className={styles.container}>
        {display.hasBrokerInfo && (
          <section className={styles.group} aria-labelledby="source-info-broker-heading">
            <h3 id="source-info-broker-heading" className={styles.groupTitle}>
              {t('sourceBrokerHeading')}
            </h3>
            <dl className={styles.detailList}>
              {display.brokerRows.map((row) => (
                <div key={row.labelKey} className={styles.detailRow}>
                  <dt className={styles.label}>{t(row.labelKey)}</dt>
                  <dd className={styles.value}>{row.valueKey ? t(row.valueKey) : row.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {display.hasMlsInfo && (
          <section className={styles.group} aria-labelledby="source-info-mls-heading">
            <div className={styles.mlsHeader}>
              {display.mlsLogo && (
                <FederatedPhoto
                  src={display.mlsLogo}
                  alt={display.mlsName ? '' : mlsHeading}
                  className={styles.mlsLogo}
                  defaultSize={MLS_LOGO_SIZE}
                  fit="contain"
                  loading="lazy"
                  preserveSource={true}
                />
              )}
              <h3 id="source-info-mls-heading" className={styles.groupTitle}>
                {mlsHeading}
              </h3>
            </div>
            {display.disclaimerParagraphs.map((paragraph) => (
              <p key={paragraph} className={styles.disclaimerText}>
                {paragraph}
              </p>
            ))}
          </section>
        )}
      </div>
    </ShowMoreContent>
  );
};
